// Color utilities + Publisher-like palettes (scheme colors, standard colors, PANTONE approximations)
import { normalizeHex, hexWithAlpha, tint, shade, hexToHsl, hslToHex } from './colorMath';
import { gradientToCss } from './gradients';

export * from './colorMath';

// Office-like scheme (Publisher default "Office" scheme): 8 base columns + white/light gray
export const SCHEME_BASE = [
  { name: 'Black, Text 1', hex: '#000000' },
  { name: 'Dark Blue, Text 2', hex: '#1f3763' },
  { name: 'Red, Accent 1', hex: '#c0392b' },
  { name: 'Blue-Gray, Accent 2', hex: '#4a6d8c' },
  { name: 'Cream, Accent 3', hex: '#f3e9c6' },
  { name: 'Navy, Accent 4', hex: '#1b2a49' },
  { name: 'Plum, Accent 5', hex: '#7b2c6f' },
  { name: 'White, Background 1', hex: '#ffffff' },
];

export const SCHEME_PRESETS = {
  office: SCHEME_BASE,
  bluebird: [
    { name: 'Black', hex: '#000000' }, { name: 'Dark Teal', hex: '#0b3c49' }, { name: 'Sky', hex: '#2a9d8f' }, { name: 'Sand', hex: '#e9c46a' },
    { name: 'Orange', hex: '#f4a261' }, { name: 'Brick', hex: '#e76f51' }, { name: 'Slate', hex: '#264653' }, { name: 'White', hex: '#ffffff' },
  ],
  harvest: [
    { name: 'Black', hex: '#000000' }, { name: 'Brown', hex: '#5c3a21' }, { name: 'Pumpkin', hex: '#d9642b' }, { name: 'Gold', hex: '#e0a100' },
    { name: 'Olive', hex: '#6b7a2a' }, { name: 'Maroon', hex: '#7a1f2b' }, { name: 'Cream', hex: '#f6e7c1' }, { name: 'White', hex: '#ffffff' },
  ],
  modern: [
    { name: 'Black', hex: '#000000' }, { name: 'Charcoal', hex: '#2b2d42' }, { name: 'Violet', hex: '#7c3aed' }, { name: 'Pink', hex: '#db2777' },
    { name: 'Cyan', hex: '#06b6d4' }, { name: 'Emerald', hex: '#10b981' }, { name: 'Amber', hex: '#f59e0b' }, { name: 'White', hex: '#ffffff' },
  ],
};

/** Legacy id of a publication's single picture-built scheme (7.17); new schemes get `custom:<uuid>` ids. */
export const CUSTOM_SCHEME_ID = 'custom';
export const isCustomSchemeId = (id) => id === CUSTOM_SCHEME_ID || String(id || '').startsWith('custom:');
/** Built-in scheme ids (everything in SCHEME_PRESETS that is not a publication's own scheme). */
export const builtinSchemeIds = () => Object.keys(SCHEME_PRESETS).filter((id) => !isCustomSchemeId(id));
/**
 * Register the publication's own schemes ([{ id, name, colors[8] }]) in SCHEME_PRESETS so every lookup (pickers, WordArt,
 * building blocks, recolouring) sees them; schemes no longer in the list are removed.
 */
export const registerCustomSchemes = (schemes) => {
  const keep = new Set();
  (schemes || []).forEach((s) => {
    if (!s || !isCustomSchemeId(s.id) || !Array.isArray(s.colors) || s.colors.length !== 8) return;
    SCHEME_PRESETS[s.id] = s.colors.map((c) => ({ name: c.name || '', hex: normalizeHex(c.hex) }));
    keep.add(s.id);
  });
  Object.keys(SCHEME_PRESETS).forEach((id) => { if (isCustomSchemeId(id) && !keep.has(id)) delete SCHEME_PRESETS[id]; });
};
/** Add one scheme to SCHEME_PRESETS without touching the others (custom templates carrying their own schemes). */
export const ensureSchemeRegistered = (scheme) => {
  if (scheme && isCustomSchemeId(scheme.id) && Array.isArray(scheme.colors) && scheme.colors.length === 8) SCHEME_PRESETS[scheme.id] = scheme.colors.map((c) => ({ name: c.name || '', hex: normalizeHex(c.hex) }));
};
/** Back-compat: register / clear a single scheme under the legacy id. */
export const registerCustomScheme = (scheme) => registerCustomSchemes(scheme ? [{ ...scheme, id: CUSTOM_SCHEME_ID }] : []);
/** Display name of a scheme id ("office" → "Office"; custom schemes use their own name). */
export const schemeLabel = (id, customSchemes) => {
  if (isCustomSchemeId(id)) {
    const list = Array.isArray(customSchemes) ? customSchemes : customSchemes ? [customSchemes] : [];
    return list.find((s) => s.id === id || (!s.id && id === CUSTOM_SCHEME_ID))?.name || 'Custom';
  }
  return String(id || '').charAt(0).toUpperCase() + String(id || '').slice(1);
};

/** Returns 5 rows of tints/shades for a base color (like Office) */
export const schemeVariants = (hex) => {
  const { l } = hexToHsl(hex);
  if (l > 92) return [shade(hex, 0.05), shade(hex, 0.15), shade(hex, 0.25), shade(hex, 0.35), shade(hex, 0.5)];
  if (l < 8) return [tint(hex, 0.5), tint(hex, 0.35), tint(hex, 0.25), tint(hex, 0.15), tint(hex, 0.05)];
  return [tint(hex, 0.8), tint(hex, 0.6), tint(hex, 0.4), shade(hex, 0.25), shade(hex, 0.5)];
};

export const STANDARD_COLORS = [
  { name: 'Dark Red', hex: '#c00000' }, { name: 'Red', hex: '#ff0000' }, { name: 'Orange', hex: '#ffc000' }, { name: 'Yellow', hex: '#ffff00' },
  { name: 'Light Green', hex: '#92d050' }, { name: 'Green', hex: '#00b050' }, { name: 'Light Blue', hex: '#00b0f0' }, { name: 'Blue', hex: '#0070c0' },
  { name: 'Dark Blue', hex: '#002060' }, { name: 'Purple', hex: '#7030a0' },
];

// Standard tab honeycomb (Office "Standard" colors tab): 127-ish colors, we use a 12x10 grid of hue x lightness
export const STANDARD_GRID = (() => {
  const rows = [];
  const lightnesses = [92, 84, 76, 66, 56, 46, 38, 30, 22, 14];
  for (const l of lightnesses) {
    const row = [];
    for (let i = 0; i < 12; i++) row.push(hslToHex({ h: i * 30, s: 85, l }));
    rows.push(row);
  }
  rows.push(['#ffffff', '#f2f2f2', '#d9d9d9', '#bfbfbf', '#a6a6a6', '#8c8c8c', '#737373', '#595959', '#404040', '#262626', '#0d0d0d', '#000000']);
  return rows;
})();

// PANTONE(R) solid coated approximations (sRGB). Names are trademarks of Pantone LLC.
export const PANTONE_COLORS = [
  ['PANTONE Yellow C', '#fedd00'], ['PANTONE Yellow 012 C', '#ffd700'], ['PANTONE Orange 021 C', '#fe5000'], ['PANTONE Warm Red C', '#f9423a'],
  ['PANTONE Red 032 C', '#ef3340'], ['PANTONE Rubine Red C', '#ce0058'], ['PANTONE Rhodamine Red C', '#e10098'], ['PANTONE Purple C', '#bb29bb'],
  ['PANTONE Violet C', '#440099'], ['PANTONE Blue 072 C', '#10069f'], ['PANTONE Reflex Blue C', '#001489'], ['PANTONE Process Blue C', '#0085ca'],
  ['PANTONE Green C', '#00ab84'], ['PANTONE Black C', '#2d2926'], ['PANTONE 100 C', '#f6eb61'], ['PANTONE 101 C', '#f7ea48'],
  ['PANTONE 102 C', '#fce300'], ['PANTONE 106 C', '#f9e547'], ['PANTONE 109 C', '#ffd100'], ['PANTONE 116 C', '#ffcd00'],
  ['PANTONE 123 C', '#ffc72c'], ['PANTONE 130 C', '#f2a900'], ['PANTONE 137 C', '#ff9e1b'], ['PANTONE 144 C', '#ed8b00'],
  ['PANTONE 151 C', '#ff8200'], ['PANTONE 158 C', '#e87722'], ['PANTONE 165 C', '#ff671f'], ['PANTONE 172 C', '#fa4616'],
  ['PANTONE 178 C', '#ff585d'], ['PANTONE 185 C', '#e4002b'], ['PANTONE 186 C', '#c8102e'], ['PANTONE 187 C', '#a6192e'],
  ['PANTONE 199 C', '#d50032'], ['PANTONE 200 C', '#ba0c2f'], ['PANTONE 202 C', '#862633'], ['PANTONE 206 C', '#ce0037'],
  ['PANTONE 212 C', '#f04e98'], ['PANTONE 219 C', '#da1884'], ['PANTONE 226 C', '#d0006f'], ['PANTONE 233 C', '#c6007e'],
  ['PANTONE 241 C', '#af1685'], ['PANTONE 2592 C', '#9b26b6'], ['PANTONE 266 C', '#753bbd'], ['PANTONE 2685 C', '#56008c'],
  ['PANTONE 2728 C', '#0047bb'], ['PANTONE 2745 C', '#3c1053'], ['PANTONE 286 C', '#0032a0'], ['PANTONE 293 C', '#003da5'],
  ['PANTONE 294 C', '#002f6c'], ['PANTONE 2925 C', '#009cde'], ['PANTONE 2965 C', '#00263e'], ['PANTONE 300 C', '#005eb8'],
  ['PANTONE 3005 C', '#0077c8'], ['PANTONE 306 C', '#00b5e2'], ['PANTONE 3115 C', '#00c1d5'], ['PANTONE 320 C', '#009ca6'],
  ['PANTONE 3262 C', '#00bfb3'], ['PANTONE 3272 C', '#00a499'], ['PANTONE 340 C', '#00965e'], ['PANTONE 347 C', '#009a44'],
  ['PANTONE 354 C', '#00b140'], ['PANTONE 361 C', '#43b02a'], ['PANTONE 368 C', '#78be20'], ['PANTONE 375 C', '#97d700'],
  ['PANTONE 376 C', '#84bd00'], ['PANTONE 382 C', '#c4d600'], ['PANTONE 448 C', '#4a412a'], ['PANTONE 4625 C', '#4f2c1d'],
  ['PANTONE 468 C', '#ddcba4'], ['PANTONE 476 C', '#4e3629'], ['PANTONE 549 C', '#6ba4b8'], ['PANTONE 5493 C', '#5c8f92'],
  ['PANTONE 7462 C', '#00558c'], ['PANTONE 7473 C', '#279989'], ['PANTONE 7482 C', '#009f4d'], ['PANTONE 7495 C', '#8f993e'],
  ['PANTONE 7502 C', '#ceb888'], ['PANTONE 7548 C', '#ffc600'], ['PANTONE 7687 C', '#1d4f91'], ['PANTONE 7738 C', '#48a23f'],
  ['PANTONE 802 C', '#44d62c'], ['PANTONE 803 C', '#ffe900'], ['PANTONE 804 C', '#ffaa4d'], ['PANTONE 805 C', '#ff7276'],
  ['PANTONE 806 C', '#ff3eb5'], ['PANTONE 807 C', '#ea27c2'], ['PANTONE 871 C (Gold)', '#84754e'], ['PANTONE 877 C (Silver)', '#8a8d8f'],
  ['PANTONE Cool Gray 1 C', '#d9d9d6'], ['PANTONE Cool Gray 3 C', '#c8c9c7'], ['PANTONE Cool Gray 5 C', '#b1b3b3'], ['PANTONE Cool Gray 7 C', '#97999b'],
  ['PANTONE Cool Gray 9 C', '#75787b'], ['PANTONE Cool Gray 11 C', '#53565a'], ['PANTONE Warm Gray 1 C', '#d7d2cb'], ['PANTONE Warm Gray 6 C', '#a59c94'],
  ['PANTONE Warm Gray 11 C', '#6e6259'],
].map(([name, hex]) => ({ name, hex }));

export const RECENT_COLORS_KEY = 'wp.recentColors';
export const getRecentColors = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_COLORS_KEY) || '[]');
  } catch {
    return [];
  }
};
export const pushRecentColor = (hex) => {
  if (!hex || hex === 'transparent') return getRecentColors();
  const n = normalizeHex(hex);
  const list = [n, ...getRecentColors().filter((c) => c !== n)].slice(0, 10);
  localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(list));
  return list;
};

/** CSS string for a fill object {type, color, gradient, image} */
export const fillToCss = (fill, fallback = 'transparent') => {
  if (!fill || fill.type === 'none') return 'transparent';
  if (fill.type === 'gradient' && fill.gradient) return gradientToCss(fill.gradient);
  if (fill.type === 'image' && fill.image?.url) {
    if (fill.image.fit === 'tile') return `url(${fill.image.url}) 0 0 / auto repeat`;
    return `url(${fill.image.url}) center / ${fill.image.fit === 'contain' ? 'contain' : fill.image.fit === 'stretch' ? '100% 100%' : 'cover'} no-repeat`;
  }
  if (fill.type === 'pattern' && fill.pattern) return patternCss(fill.pattern, fill.color || '#000000', fill.background || '#ffffff');
  if (fill.color) return fill.transparency ? hexWithAlpha(fill.color, 100 - fill.transparency) : fill.color;
  return fallback;
};

export const patternCss = (pattern, fg, bg) => {
  switch (pattern) {
    case 'dots': return `radial-gradient(${fg} 1.5px, ${bg} 1.5px) 0 0 / 8px 8px`;
    case 'stripes': return `repeating-linear-gradient(45deg, ${fg} 0 3px, ${bg} 3px 9px)`;
    case 'hstripes': return `repeating-linear-gradient(0deg, ${fg} 0 3px, ${bg} 3px 9px)`;
    case 'vstripes': return `repeating-linear-gradient(90deg, ${fg} 0 3px, ${bg} 3px 9px)`;
    case 'grid': return `linear-gradient(${fg} 1px, transparent 1px) 0 0 / 10px 10px, linear-gradient(90deg, ${fg} 1px, ${bg} 1px) 0 0 / 10px 10px`;
    case 'checker': return `repeating-conic-gradient(${fg} 0 25%, ${bg} 0 50%) 0 0 / 12px 12px`;
    case 'diamonds': return `repeating-conic-gradient(from 45deg, ${fg} 0 25%, ${bg} 0 50%) 0 0 / 12px 12px`;
    default: return bg;
  }
};

export const PATTERNS = ['dots', 'stripes', 'hstripes', 'vstripes', 'grid', 'checker', 'diamonds'];


/** What a ribbon swatch shows for a fill: the colour, the gradient / pattern / picture CSS, or null for "no fill". */
export const fillSwatchCss = (fill) => {
  if (!fill || fill.type === 'none') return null;
  if (fill.type === 'solid' || !fill.type) return fill.color || null;
  const css = fillToCss(fill, null);
  return css && css !== 'transparent' ? css : null;
};
